/*
 * Settings, Price list: what the auto quoter prices from (@steward/shared prices.ts). Typed once by the owner. The list is
 * one setting, so a location gets the brand's list until it saves its own.
 */
import {
  HOURLY_CENTS,
  PRICE_OFTEN_WORDS,
  cardToBook,
  POLICY_NAMES,
  priceList,
  type Settings,
  type PriceExtra,
  type PriceList,
  type PriceRate,
  type PriceService,
  type RepeatOften,
} from "@steward/shared";
import { Fragment, useState } from "react";
import { useSettings, useUpdateSettings } from "../../api/hooks";
import { useSession } from "../../app/session";
import { Combobox, InputText } from "../../components/fields";
import { money } from "../../components/format";
import { useToast } from "../../components/overlays";
import { Banner, Button, Card, IconButton, Skeleton, Switch } from "../../components/parts";
import { errText, LineMore } from "../../components/work";

/** Rates as typed: dollars for rooms, minimums and add-ons; cents for a square foot. Empty means no price. */
interface RateText {
  perRoom: string;
  perSqft: string;
  min: string;
}
interface ServiceText extends RateText {
  id: string;
  name: string;
  repeats: boolean;
  about: string;
  link: string;
}
interface ExtraText {
  id: string;
  name: string;
  price: string;
  /** The flat price on a repeating plan; empty for the usual price. */
  plan: string;
  each: boolean;
  only: string;
  about: string;
  link: string;
}

const dollars = (c: number | null) => (c === null ? "" : (c / 100).toFixed(2).replace(/\.00$/, ""));
const rateText = (r: PriceRate): RateText => ({
  perRoom: dollars(r.perRoomCents),
  perSqft: r.perSqftCents === null ? "" : String(r.perSqftCents),
  min: r.minCents ? dollars(r.minCents) : "",
});
const cents = (s: string) => {
  const n = Number(s.replace(/[$,\s]/g, ""));
  return s.trim() && Number.isFinite(n) ? Math.round(n * 100) : null;
};
const rateOut = (r: RateText): PriceRate => ({
  perRoomCents: cents(r.perRoom),
  perSqftCents: r.perSqft.trim() ? Number(r.perSqft) : null,
  minCents: cents(r.min) ?? 0,
});
let nextId = 0;
const newId = (letter: string, taken: string[]) => {
  let id = "";
  do id = `${letter}${String(++nextId)}`;
  while (taken.includes(id));
  return id;
};
const REPEATS: RepeatOften[] = ["weekly", "biweekly", "monthly"];
/** What it covers and its page, left out when empty so an old list reads the same. */
const aboutOut = (x: { about: string; link: string }) => ({
  ...(x.about.trim() ? { about: x.about.trim() } : {}),
  ...(x.link.trim() ? { link: x.link.trim() } : {}),
});

export function PriceSettings() {
  const { company, can } = useSession();
  const s = useSettings(company.id);
  const [editing, setEditing] = useState(false);
  if (!s.data) return <Skeleton rows={6} />;
  const list = s.data.settings.prices;
  const own = "prices" in s.data.own;
  const editable = can("settings.update");
  if (editing) return <PriceEdit list={list} onClose={() => setEditing(false)} />;
  const rate = (r: PriceRate) =>
    [
      r.perRoomCents !== null ? `${money(r.perRoomCents)} a room` : null,
      r.perSqftCents !== null ? `${String(r.perSqftCents)}¢ a sq ft` : null,
      r.minCents ? `at least ${money(r.minCents)}` : null,
    ]
      .filter(Boolean)
      .join(", ") || "Not offered";
  return (
    <>
      <Card
        title="Price list"
        action={
          editable ? (
            <Button type="secondary" size="small" onClick={() => setEditing(true)}>
              Edit
            </Button>
          ) : null
        }
        flush
      >
        {list.services.length ? (
          <table className="data lines">
            <tbody>
              {list.services.map((sv) => (
                <tr key={sv.id}>
                  <td>
                    <b>{sv.name}</b>
                    {sv.repeats ? null : <span className="muted"> · one time only</span>}
                    <LineMore line={{ note: sv.about, link: sv.link }} basis={false} />
                  </td>
                  <td className="right">{rate(sv)}</td>
                </tr>
              ))}
              {REPEATS.map((o) => (
                <tr key={o}>
                  <td>{PRICE_OFTEN_WORDS[o]}, visits after the first</td>
                  <td className="right">{rate(list.repeat[o])}</td>
                </tr>
              ))}
              <tr>
                <td>Maid service, by the hour</td>
                <td className="right">{money(list.hourlyCents ?? HOURLY_CENTS)} an hour for each cleaner</td>
              </tr>
              {list.extras.map((e) => (
                <tr key={e.id}>
                  <td>
                    {e.name}
                    <span className="muted">
                      {" "}
                      · add-on{e.only ? `, ${list.services.find((x) => x.id === e.only)?.name ?? ""} only` : ""}
                    </span>
                    <LineMore line={{ note: e.about, link: e.link }} basis={false} />
                  </td>
                  <td className="right">
                    {money(e.priceCents)}
                    {e.each ? " each" : ""}
                    {e.planCents != null && e.planCents !== e.priceCents ? (
                      <span className="muted">, plan {money(e.planCents)}</span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="card-empty muted">
            No prices yet. Add your services once and every quote prices itself from the size of the home.
          </div>
        )}
      </Card>
      <p className="muted small">
        Rooms are bedrooms plus bathrooms plus {list.baseRooms} for the kitchen and living area. A half bath counts as a bathroom.
        Add-ons are on the first visit only.
        {own ? "" : " This list comes from above until you save your own."}
      </p>
      <ApprovalSettings />
      <PolicySettings />
      <BookingSettings />
    </>
  );
}

/** What a client must do to approve a quote (like Jobber): save a card and sign by hand. Both on unless turned off. */
function ApprovalSettings() {
  const { company, can } = useSession();
  const s = useSettings(company.id);
  const save = useUpdateSettings();
  const toast = useToast();
  if (!s.data) return null;
  const a = s.data.settings.quoteApproval;
  const set = (patch: Partial<typeof a>) =>
    save.mutate(
      { quoteApproval: patch },
      { onSuccess: () => toast("Saved."), onError: (e) => toast(errText(e), { kind: "error" }) },
    );
  const editable = can("settings.update");
  return (
    <Card title="Approving a quote">
      <div className="stack">
        <label className="row">
          <Switch checked={a.card} disabled={!editable} onChange={(v) => set({ card: v })} label="Card on file to approve" />
          <span>
            Card on file to approve
            <span className="muted small" style={{ display: "block" }}>
              The client saves a card before they can approve. It is charged only after each visit.
            </span>
          </span>
        </label>
        <label className="row">
          <Switch checked={a.sign} disabled={!editable} onChange={(v) => set({ sign: v })} label="Signature to approve" />
          <span>
            Signature to approve
            <span className="muted small" style={{ display: "block" }}>
              The client signs with a finger or mouse. You see the signature on the quote.
            </span>
          </span>
        </label>
      </div>
    </Card>
  );
}

/**
 * The policies clients agree to when they sign (owner, 2026-10-31). Shown above the signature box, saved word for word
 * with each signature, and used in the dispute file. An empty one is not shown. Changes count for new signatures only.
 */
function PolicySettings() {
  const { company, can } = useSession();
  const s = useSettings(company.id);
  const save = useUpdateSettings();
  const toast = useToast();
  const [draft, setDraft] = useState<Settings["policies"] | null>(null);
  if (!s.data) return null;
  const p = s.data.settings.policies;
  const editable = can("settings.update");
  return (
    <Card
      id="policies"
      title="Policies clients sign"
      action={
        editable && !draft ? (
          <Button type="secondary" size="small" icon="edit" onClick={() => setDraft({ ...p })}>
            Edit
          </Button>
        ) : null
      }
    >
      {draft ? (
        <div className="stack">
          {POLICY_NAMES.map(([key, title]) => (
            <label key={key} className="field wide">
              <span className="label">{title}</span>
              <textarea
                rows={4}
                maxLength={3000}
                value={draft[key]}
                onChange={(x) => setDraft({ ...draft, [key]: x.target.value })}
              />
            </label>
          ))}
          <InputText
            label="Hours a client has to tell you something is wrong"
            description="The guarantee window. The dispute file checks each complaint against it."
            type="number"
            value={String(draft.complaintHours)}
            onChange={(x) =>
              setDraft({ ...draft, complaintHours: Math.max(1, Math.min(720, Math.round(Number(x.target.value) || 24))) })
            }
          />
          <div className="row end">
            <Button type="tertiary" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button
              loading={save.isPending}
              onClick={() =>
                save.mutate(
                  { policies: draft },
                  {
                    onSuccess: () => {
                      setDraft(null);
                      toast("Saved. New signatures get these words.");
                    },
                    onError: (x) => toast(errText(x), { kind: "error" }),
                  },
                )
              }
            >
              Save
            </Button>
          </div>
        </div>
      ) : (
        <div className="stack">
          <p className="muted small">
            Clients see these above the signature box on every quote and job confirm. Each signature keeps the exact words, so a
            dispute file shows what that client agreed to.
          </p>
          {POLICY_NAMES.map(([key, title]) => (
            <div key={key}>
              <strong>{title}</strong>
              <p className={p[key].trim() ? undefined : "muted"}>{p[key].trim() || "Not shown to clients"}</p>
            </div>
          ))}
          <p className="muted small">Complaint window {String(p.complaintHours)} hours</p>
        </div>
      )}
    </Card>
  );
}

/** Booking from a quote: the client picks a day on the quote page and it goes on the schedule, a card first if asked. */
function BookingSettings() {
  const { company, can } = useSession();
  const s = useSettings(company.id);
  const save = useUpdateSettings();
  const toast = useToast();
  if (!s.data) return null;
  const b = s.data.settings.booking;
  const always = s.data.settings.quoteApproval.card;
  const card = always || cardToBook(s.data.settings, company.trade);
  const set = (patch: Partial<typeof b>) =>
    save.mutate({ booking: patch }, { onSuccess: () => toast("Saved."), onError: (e) => toast(errText(e), { kind: "error" }) });
  const editable = can("settings.update");
  return (
    <Card title="Booking from a quote">
      <div className="stack">
        <label className="row">
          <Switch checked={b.fromQuote} disabled={!editable} onChange={(v) => set({ fromQuote: v })} label="Auto scheduling" />
          <span>
            Auto scheduling
            <span className="muted small" style={{ display: "block" }}>
              Clients see your open days, with morning and afternoon room, and book one on the quote. Booking approves it and puts
              the job on the schedule. Off, clients approve the quote and you set the day.
            </span>
            {b.fromQuote ? (
              <span className="muted small" style={{ display: "block" }}>
                Capacity stays on with it, so full days and times close. Set your teams in{" "}
                <a href="/schedule/settings#capacity">Schedule settings</a>.
              </span>
            ) : null}
          </span>
        </label>
        <label className="row">
          <Switch
            checked={card}
            disabled={!editable || !b.fromQuote || always}
            onChange={(v) => set({ cardFirst: v })}
            label="Save a card to hold the day"
          />
          <span>
            Save a card to hold the day
            <span className="muted small" style={{ display: "block" }}>
              {always ? "On, since every quote needs a card to approve." : "The card is charged only after the visit."}
            </span>
          </span>
        </label>
      </div>
    </Card>
  );
}

function PriceEdit({ list, onClose }: { list: PriceList; onClose: () => void }) {
  const save = useUpdateSettings();
  const toast = useToast();
  const [baseRooms, setBaseRooms] = useState(String(list.baseRooms));
  const [hourly, setHourly] = useState(dollars(list.hourlyCents ?? HOURLY_CENTS));
  const [services, setServices] = useState<ServiceText[]>(() =>
    list.services.map((s) => ({
      id: s.id,
      name: s.name,
      repeats: s.repeats,
      about: s.about ?? "",
      link: s.link ?? "",
      ...rateText(s),
    })),
  );
  const [repeat, setRepeat] = useState<Record<RepeatOften, RateText>>(() => ({
    weekly: rateText(list.repeat.weekly),
    biweekly: rateText(list.repeat.biweekly),
    monthly: rateText(list.repeat.monthly),
  }));
  const [extras, setExtras] = useState<ExtraText[]>(() =>
    list.extras.map((e) => ({
      id: e.id,
      name: e.name,
      price: dollars(e.priceCents),
      plan: dollars(e.planCents ?? null),
      each: e.each,
      only: e.only ?? "",
      about: e.about ?? "",
      link: e.link ?? "",
    })),
  );
  const [error, setError] = useState<string | null>(null);
  const setService = (i: number, patch: Partial<ServiceText>) =>
    setServices(services.map((s, n) => (n === i ? { ...s, ...patch } : s)));
  const setExtra = (i: number, patch: Partial<ExtraText>) => setExtras(extras.map((e, n) => (n === i ? { ...e, ...patch } : e)));
  const go = () => {
    const out: PriceList = {
      baseRooms: Number(baseRooms) || 0,
      services: services
        .filter((s) => s.name.trim())
        .map((s): PriceService => ({ id: s.id, name: s.name.trim(), repeats: s.repeats, ...aboutOut(s), ...rateOut(s) })),
      repeat: { weekly: rateOut(repeat.weekly), biweekly: rateOut(repeat.biweekly), monthly: rateOut(repeat.monthly) },
      extras: extras
        .filter((e) => e.name.trim())
        .map((e): PriceExtra => ({
          id: e.id,
          name: e.name.trim(),
          priceCents: cents(e.price) ?? 0,
          planCents: cents(e.plan),
          each: e.each,
          only: services.some((s) => s.id === e.only) ? e.only : null,
          ...aboutOut(e),
        })),
      hourlyCents: cents(hourly) ?? HOURLY_CENTS,
    };
    const checked = priceList.safeParse(out);
    if (!checked.success) {
      setError(checked.error.issues[0]?.message ?? "Something in the list is not right.");
      return;
    }
    if (out.services.some((s) => s.perRoomCents === null && s.perSqftCents === null && !s.minCents)) {
      setError("Give every service a room price, a square foot price or a lowest price.");
      return;
    }
    save.mutate(
      { prices: checked.data },
      {
        onSuccess: () => {
          toast("Price list saved");
          onClose();
        },
        onError: (e) => setError(errText(e)),
      },
    );
  };
  const rateFields = (r: RateText, set: (r: RateText) => void) => (
    <>
      <InputText
        label="A room"
        prefix="$"
        inputMode="decimal"
        value={r.perRoom}
        onChange={(e) => set({ ...r, perRoom: e.target.value })}
      />
      <InputText
        label="A sq ft"
        suffix="¢"
        inputMode="decimal"
        value={r.perSqft}
        onChange={(e) => set({ ...r, perSqft: e.target.value })}
      />
      <InputText
        label="Lowest price"
        prefix="$"
        inputMode="decimal"
        value={r.min}
        onChange={(e) => set({ ...r, min: e.target.value })}
      />
    </>
  );
  return (
    <>
      {error ? <Banner type="error">{error}</Banner> : null}
      <Card title="Services">
        <div className="stack">
          <p className="muted small">
            The first visit is priced here. Leave a box empty when you do not price that way. A square foot price is in cents.
          </p>
          {services.map((s, i) => (
            <Fragment key={s.id}>
              <div className="price-row">
                <InputText
                  label="Service"
                  value={s.name}
                  placeholder="Deep cleaning"
                  onChange={(e) => setService(i, { name: e.target.value })}
                />
                {rateFields(s, (r) => setService(i, r))}
                <label className="row price-switch">
                  <Switch
                    checked={s.repeats}
                    onChange={(v) => setService(i, { repeats: v })}
                    label={`${s.name || "This service"} can repeat`}
                  />
                  <span className="small">Can repeat</span>
                </label>
                <IconButton
                  icon="trash"
                  label="Remove service"
                  size={18}
                  onClick={() => setServices(services.filter((_, n) => n !== i))}
                />
              </div>
              <AboutFields x={s} set={(p) => setService(i, p)} />
            </Fragment>
          ))}
          <div>
            <Button
              type="tertiary"
              size="small"
              icon="plus"
              onClick={() =>
                setServices([
                  ...services,
                  {
                    id: newId(
                      "s",
                      services.map((x) => x.id),
                    ),
                    name: "",
                    repeats: true,
                    about: "",
                    link: "",
                    perRoom: "",
                    perSqft: "",
                    min: "",
                  },
                ])
              }
            >
              Add service
            </Button>
          </div>
        </div>
      </Card>
      <Card title="Repeat visits">
        <div className="stack">
          <p className="muted small">Every visit after the first is priced here. Leave a schedule empty to not offer it.</p>
          {REPEATS.map((o) => (
            <div className="price-row repeat" key={o}>
              <span className="price-label">{PRICE_OFTEN_WORDS[o]}</span>
              {rateFields(repeat[o], (r) => setRepeat({ ...repeat, [o]: r }))}
            </div>
          ))}
        </div>
      </Card>
      <Card title="Add-ons">
        <div className="stack">
          {extras.map((e, i) => (
            <Fragment key={e.id}>
              <div className="price-row extra">
                <InputText
                  label="Add-on"
                  value={e.name}
                  placeholder="Inside the fridge"
                  onChange={(ev) => setExtra(i, { name: ev.target.value })}
                />
                <InputText
                  label="Price"
                  prefix="$"
                  inputMode="decimal"
                  value={e.price}
                  onChange={(ev) => setExtra(i, { price: ev.target.value })}
                />
                <InputText
                  label="Plan price"
                  prefix="$"
                  inputMode="decimal"
                  placeholder={e.price}
                  description="For clients on weekly, every 2 weeks or monthly. Empty is the usual price."
                  value={e.plan}
                  onChange={(ev) => setExtra(i, { plan: ev.target.value })}
                />
                <Combobox
                  label="For"
                  value={e.only}
                  onChange={(v) => setExtra(i, { only: v })}
                  options={[
                    { value: "", label: "Every service" },
                    ...services.filter((s) => s.name.trim()).map((s) => ({ value: s.id, label: s.name })),
                  ]}
                />
                <label className="row price-switch">
                  <Switch
                    checked={e.each}
                    onChange={(v) => setExtra(i, { each: v })}
                    label={`${e.name || "This add-on"} is priced each`}
                  />
                  <span className="small">Priced each</span>
                </label>
                <IconButton
                  icon="trash"
                  label="Remove add-on"
                  size={18}
                  onClick={() => setExtras(extras.filter((_, n) => n !== i))}
                />
              </div>
              <AboutFields x={e} set={(p) => setExtra(i, p)} />
            </Fragment>
          ))}
          <div>
            <Button
              type="tertiary"
              size="small"
              icon="plus"
              onClick={() =>
                setExtras([
                  ...extras,
                  {
                    id: newId(
                      "x",
                      extras.map((x) => x.id),
                    ),
                    name: "",
                    price: "",
                    plan: "",
                    each: false,
                    only: "",
                    about: "",
                    link: "",
                  },
                ])
              }
            >
              Add add-on
            </Button>
          </div>
        </div>
      </Card>
      <Card title="Maid service by the hour">
        <InputText
          label="An hour for each cleaner"
          prefix="$"
          inputMode="decimal"
          value={hourly}
          description="Quotes priced by the hour are cleaners times hours times this."
          onChange={(e) => setHourly(e.target.value)}
        />
      </Card>
      <Card title="Rooms every home has">
        <InputText
          label="Counted on top of bedrooms and bathrooms"
          inputMode="numeric"
          value={baseRooms}
          description="Usually 2, the kitchen and the living area."
          onChange={(e) => setBaseRooms(e.target.value)}
        />
      </Card>
      <div className="row end">
        <Button type="tertiary" onClick={onClose}>
          Cancel
        </Button>
        <Button loading={save.isPending} onClick={go}>
          Save price list
        </Button>
      </div>
    </>
  );
}

/** Under a service or add-on: what it covers and a page that lists what is included. Both go on the quote line. */
function AboutFields({
  x,
  set,
}: {
  x: { about: string; link: string };
  set: (patch: { about?: string; link?: string }) => void;
}) {
  return (
    <div className="price-about">
      <InputText
        label="What it covers"
        value={x.about}
        maxLength={300}
        placeholder="Kitchen, bathrooms, dusting, floors and trash"
        onChange={(e) => set({ about: e.target.value })}
      />
      <InputText
        label="What's included page"
        type="url"
        inputMode="url"
        value={x.link}
        placeholder="https://thecleanersusa.com/deep-cleaning"
        onChange={(e) => set({ link: e.target.value })}
      />
    </div>
  );
}
